import { useEffect, useRef } from 'react'

type Props = {
  src: string
  /** O nome do produto — vira o `alt` e o rótulo do diálogo. */
  nome: string
  onFechar: () => void
}

/**
 * A foto do produto em tamanho grande.
 *
 * ## O `alt` aqui é de verdade, ao contrário do herói
 *
 * No cartão o `alt` é vazio de propósito: o nome do produto está do lado, e um
 * leitor de tela que anunciasse "foto de Coca-Cola 2L" antes de "Coca-Cola 2L"
 * diria a mesma coisa duas vezes. Aqui a imagem **é** o conteúdo — foi o que a
 * pessoa pediu para ver —, então ela se descreve.
 *
 * ## Preto quase opaco, e não vidro
 *
 * O fundo desta tela não é uma superfície do app: é o que sai da frente para a
 * foto aparecer. Vidro desfocado atrás de uma imagem competiria com ela — e
 * ainda gastaria um `frost` do orçamento (ver `Glass`) numa tela cujo único
 * conteúdo é a foto.
 *
 * ## `object-contain`, nunca `cover`
 *
 * Uma embalagem é alta e estreita; `cover` cortaria justamente as pontas — o
 * bico da garrafa, o rótulo do fundo — que é onde mora o que a pessoa abriu a
 * foto para conferir.
 */
export function FotoAmpliada({ src, nome, onFechar }: Props) {
  const fechar = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    fechar.current?.focus()
  }, [])

  // Escape fecha. Sem isso, uma foto aberta com o teclado cobre a tela e nada
  // devolve o foco — mesma regra da `FolhaDeBaixo`.
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') onFechar()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [onFechar])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Foto de ${nome}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/92 p-4"
    >
      {/* O fundo inteiro fecha: numa tela cujo conteúdo é uma imagem, mirar um
          × de 44px seria o caminho difícil. `aria-hidden` porque o botão
          "Fechar" já é a saída anunciada. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onFechar}
        className="absolute inset-0"
      />

      <img
        src={src}
        alt={`Foto de ${nome}`}
        className="relative max-h-[88dvh] max-w-full object-contain"
      />

      <button
        ref={fechar}
        type="button"
        onClick={onFechar}
        aria-label="Fechar a foto"
        className="alvo absolute top-3 right-3 rounded-full bg-black/60 text-lg text-[var(--text)]"
      >
        ✕
      </button>
    </div>
  )
}
